import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Upload,
  Button,
  Alert,
  Space,
  message,
  Typography,
  Divider,
} from 'antd';
import { UploadOutlined, DollarOutlined, FileTextOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import dayjs from 'dayjs';
import {
  EXPENSE_TYPE_OPTIONS,
  PAYMENT_NATURE_OPTIONS,
  FILE_UPLOAD_CONFIG,
} from '../../../../constants/financeConstants';
import {
  createFinanceRecord,
  uploadAttachment,
  EntityType,
  type CreateFinanceRecordRequest,
} from '../../../../services/finance';

const { TextArea } = Input;
const { Text } = Typography;

interface AddCaseFinanceRecordModalProps {
  visible: boolean;
  caseId: number;
  onCancel: () => void;
  onSuccess: () => void;
}

const AddCaseFinanceRecordModal: React.FC<AddCaseFinanceRecordModalProps> = ({
  visible,
  caseId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log('Form values:', values);
      console.log('Current fileList:', fileList);
      setLoading(true);

      // Prepare record data
      const recordData: Omit<CreateFinanceRecordRequest, 'entity_type' | 'entity_id'> = {
        expense_type: values.expense_type,
        payment_nature: values.payment_nature,
        amount: values.amount,
        payment_time: values.payment_time
          ? dayjs(values.payment_time).format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
        remark: values.remark,
      };
      console.log('Record data:', recordData);

      // Step 1: Create finance record
      const createdRecord = await createFinanceRecord(EntityType.CASE, caseId, recordData);
      console.log('Finance record created:', createdRecord);

      // Step 2: Upload attachments if any
      if (fileList.length > 0) {
        console.log('Uploading attachments:', fileList.length, 'files');
        const uploadPromises = fileList.map((file, index) => {
          console.log(`File ${index}:`, file.name, file.originFileObj);
          if (file.originFileObj) {
            return uploadAttachment(EntityType.CASE, caseId, createdRecord.id, file.originFileObj)
              .then(result => {
                console.log(`Attachment ${index} uploaded successfully:`, result);
                return result;
              })
              .catch(error => {
                console.error(`Failed to upload attachment ${index}:`, error);
                throw error;
              });
          }
          return Promise.resolve();
        });

        await Promise.all(uploadPromises);
        console.log('All attachments uploaded successfully');
      } else {
        console.log('No attachments to upload');
      }

      message.success('财务记录添加成功');
      form.resetFields();
      setFileList([]);
      onSuccess();
    } catch (error: any) {
      console.error('Failed to create finance record:', error);
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('添加财务记录失败，请重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onCancel();
  };

  const uploadProps: UploadProps = {
    accept: FILE_UPLOAD_CONFIG.accept,
    beforeUpload: (file) => {
      console.log('beforeUpload called with file:', file.name, file.type, file.size);
      
      // Validate file size
      if (file.size > FILE_UPLOAD_CONFIG.maxSize) {
        message.error(`文件大小不能超过 ${FILE_UPLOAD_CONFIG.maxSize / 1024 / 1024}MB`);
        return Upload.LIST_IGNORE;
      }

      // Validate file type
      if (!FILE_UPLOAD_CONFIG.acceptedTypes.includes(file.type)) {
        console.error('File type not accepted:', file.type);
        message.error(`不支持的文件格式，仅支持：${FILE_UPLOAD_CONFIG.accept}`);
        return Upload.LIST_IGNORE;
      }

      // Create UploadFile object with originFileObj
      const uploadFile: UploadFile = {
        uid: file.uid || `${Date.now()}-${file.name}`,
        name: file.name,
        status: 'done',
        size: file.size,
        type: file.type,
        originFileObj: file as any,
      };

      // Use functional update to avoid stale closure
      setFileList((prevList) => {
        const newList = [...prevList, uploadFile];
        console.log('File list updated:', newList.length, 'files', newList);
        return newList;
      });
      return false; // Prevent auto upload
    },
    onRemove: (file) => {
      setFileList((prevList) => prevList.filter((f) => f.uid !== file.uid));
    },
    fileList,
  };

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined />
          <span>添加财务记录</span>
        </Space>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      okText="确定"
      cancelText="取消"
    >
      <Alert
        message="提示"
        description="财务记录一旦创建将无法修改，请仔细核对信息。如需更正，请删除后重新创建。"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
      >
        <Form.Item
          name="expense_type"
          label="费用类型"
          rules={[{ required: true, message: '请选择费用类型' }]}
        >
          <Select
            placeholder="请选择费用类型"
            options={EXPENSE_TYPE_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          name="payment_nature"
          label="款项性质"
          rules={[{ required: true, message: '请选择款项性质' }]}
        >
          <Select
            placeholder="请选择款项性质"
            options={PAYMENT_NATURE_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          name="amount"
          label="金额"
          rules={[
            { required: true, message: '请输入金额' },
            { type: 'number', min: 0.01, message: '金额必须大于0' },
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="请输入金额"
            prefix="¥"
            precision={2}
            min={0.01}
            max={999999999.99}
          />
        </Form.Item>

        <Form.Item
          name="payment_time"
          label="动款时间"
        >
          <DatePicker
            style={{ width: '100%' }}
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            placeholder="请选择动款时间"
          />
        </Form.Item>

        <Form.Item
          name="remark"
          label="备注"
        >
          <TextArea
            rows={3}
            placeholder="请输入备注信息，如付款方式、用途等"
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Divider />

        <Form.Item
          label={
            <Space>
              <FileTextOutlined />
              <span>附件</span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                （可选，支持 PDF、图片、Word、Excel 格式，单个文件不超过 20MB）
              </Text>
            </Space>
          }
        >
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>选择文件</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddCaseFinanceRecordModal;
